import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { Funnel } from 'features/co-pilot/co-pilot.types';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiRadioGroup from 'features/ui/base-components/pai-radio-group';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { LinkedinAccount } from '../linkedin-accounts/linkedin-accounts.types';
import { StartNewOrClone } from './campaign-builder.types';

interface Props {
  open: boolean;
  loading: boolean;
  nameTakenError: boolean;

  name: string;
  linkedinAccounts: LinkedinAccount[];
  selectedLinkedinAccount: string;

  startNewOrClone: StartNewOrClone;

  campaigns: Funnel[];
  selectedCampaignToClone: string;
  cloneContacts: boolean;

  handleCampaignNameChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSelectedLinkedinAccountChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleStartNewOrCloneChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSelectedCampaignToCloneChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleCloneContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;

  handleCreateCampaign(): void;

  /** Nur Recruiting: zurück in den Assistenten „Neue Kampagne“. */
  handleOpenWizard?: () => void;
}

const CreateCampaignDialog: React.FC<Props> = ({
  open,
  loading,
  nameTakenError,
  name,
  linkedinAccounts,
  selectedLinkedinAccount,
  startNewOrClone,
  campaigns,
  selectedCampaignToClone,
  cloneContacts,
  handleCampaignNameChange,
  handleSelectedLinkedinAccountChange,
  handleStartNewOrCloneChange,
  handleSelectedCampaignToCloneChange,
  handleCloneContactsChange,
  handleClose,
  handleCreateCampaign,
  handleOpenWizard,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const campaignOptions = useMemo(
    () =>
      campaigns.map((campaign) => ({
        label: campaign.name,
        value: campaign._id,
      })),
    [campaigns]
  );

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'sm'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('createNewCampaign')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={3}>
          {/* enter campaign name */}
          <Stack gap={2}>
            <Stack gap={0.5}>
              <Typography className={classes.subTitle}>{t('nameYourCampaign')}</Typography>
              <Typography className={classes.greyText}>{t('provideUniqueCampaignName')}</Typography>
            </Stack>
            <PaiTextField
              value={name}
              placeholder={t('enterCampaignNameHere')}
              variant={'background'}
              error={nameTakenError}
              errorText={t('nameAlreadyTakenError')}
              onChange={handleCampaignNameChange}
            />
          </Stack>
          <Divider />
          {/* select linkedin account */}
          <Stack gap={2}>
            <Stack gap={0.5}>
              <Stack direction={'row'} alignItems={'center'} gap={1}>
                <Typography className={classes.subTitle}>{t('selectLinkedInAccount')}</Typography>
                <PaiHelpIcon tooltipText={t('selectLinkedInAccountDescriptionHelper')} />
              </Stack>
              <Typography className={classes.greyText}>{t('selectLinkedInAccountDescription')}</Typography>
            </Stack>
            <PaiSelect value={selectedLinkedinAccount} variant={'background'} onChange={handleSelectedLinkedinAccountChange}>
              {linkedinAccounts.map((account) => (
                <PaiMenuItem key={account._id} value={account._id}>
                  {account.userEmail}
                </PaiMenuItem>
              ))}
            </PaiSelect>
          </Stack>
          <Divider />
          {/* start new or clone existing campaign */}
          <Stack gap={2}>
            <Stack gap={0.5}>
              <Typography className={classes.subTitle}>{t('startNewOrCloneCampaign')}</Typography>
              <Typography className={classes.greyText}>{t('startNewOrCloneCampaignDescription')}</Typography>
            </Stack>
            <PaiRadioGroup
              value={startNewOrClone}
              items={[
                { label: t('startNew'), value: StartNewOrClone.START_NEW },
                { label: t('cloneExisting'), value: StartNewOrClone.CLONE_EXISTING },
              ]}
              row={true}
              onChange={handleStartNewOrCloneChange}
            />
            {startNewOrClone === StartNewOrClone.CLONE_EXISTING && (
              <Typography className={classes.descriptionText}>{t('cloneCampaignDescription')}</Typography>
            )}
          </Stack>
          {startNewOrClone === StartNewOrClone.CLONE_EXISTING ? (
            <Stack gap={2}>
              <Divider />
              {/* select campaign to clone */}
              <Stack gap={2}>
                <Stack gap={0.5}>
                  <Typography className={classes.subTitle}>{t('selectCampaignToClone')}</Typography>
                  <Typography className={classes.greyText}>{t('selectCampaignToCloneDescription')}</Typography>
                </Stack>
                <PaiSelect value={selectedCampaignToClone} variant={'background'} onChange={handleSelectedCampaignToCloneChange}>
                  {campaignOptions.map((campaign) => (
                    <PaiMenuItem key={campaign.value} value={campaign.value}>
                      {campaign.label}
                    </PaiMenuItem>
                  ))}
                </PaiSelect>
              </Stack>
              <PaiCheckbox label={t('cloneContactsWithCampaign')} checked={cloneContacts} onChange={handleCloneContactsChange} />
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        {handleOpenWizard ? (
          <div className={classes.wizardLink}>
            <PaiLink text={t('wizOpenWizard')} size={'sm'} underline={'always'} action={handleOpenWizard} />
          </div>
        ) : null}
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('createCampaign')}
          border={'rounded'}
          size={'small'}
          disabled={
            name.trim() === '' || !selectedLinkedinAccount || (startNewOrClone === StartNewOrClone.CLONE_EXISTING && !selectedCampaignToClone)
          }
          loading={loading}
          action={handleCreateCampaign}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '24px !important',
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
  wizardLink: {
    marginRight: 'auto',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
    fontWeight: 'normal',
  },
  descriptionText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'normal',
  },
}));

export default CreateCampaignDialog;
